import { baseUrl } from 'App';

// The publication status of an article
export type KnowledgeBaseArticleStatus = 'concept' | 'published';

// The access level of an article
export type KnowledgeBaseArticleAccess = 'public' | 'restricted';

// The author of an article as shown to readers
export interface KnowledgeBaseAuthor {
    id: number;
    name: string;
    avatar: string | null;
}

// The icons a group can show, the keys match the icon mapping and the backend choices
export const KNOWLEDGE_BASE_GROUP_ICONS = [
    'book-open', 'books', 'rocket-launch', 'graduation-cap', 'circle-question', 'lightbulb', 'sliders', 'gears',
    'screwdriver-wrench', 'plug', 'mobile-screen', 'credit-card', 'file-invoice', 'calendar-days', 'clipboard-check',
    'chart-line', 'users', 'envelope', 'comments', 'shield-check', 'life-ring', 'puzzle-piece', 'map-location-dot',
    'truck', 'box-open', 'star',
];

// Resolves the icon mapping key of a group icon, two keys clash with existing solid icons
export const groupIconKey = (icon: string | null | undefined): string => {
    if (icon === 'screwdriver-wrench' || icon === 'truck') return `${icon}-light`;
    return icon || 'book-open';
};

// A group of the knowledge base
export interface KnowledgeBaseGroup {
    id: number;
    name: string;
    description: string | null;
    icon: string | null;
    ordering: number;
    article_count: number;
    authors?: KnowledgeBaseAuthor[];
    deleted?: boolean;
}

// A section within a group
export interface KnowledgeBaseSection {
    id: number;
    name: string;
    group: number;
    ordering: number;
    deleted?: boolean;
}

// An article without its text, as shown in lists
export interface KnowledgeBaseArticleListItem {
    id: number;
    subject: string;
    description: string | null;
    group: number;
    group_name?: string | null;
    section: number | null;
    ordering?: number;
    status?: KnowledgeBaseArticleStatus;
    access: KnowledgeBaseArticleAccess;
    feature?: string | null;
    author?: KnowledgeBaseAuthor | null;
    modified?: string;
}

// A full article as edited in the editor
export interface KnowledgeBaseArticle extends KnowledgeBaseArticleListItem {
    text: string | null;
    status: KnowledgeBaseArticleStatus;
    feature: string | null;
    feature_translation: string | null;
    packages: string[];
    created?: string;
    modified: string;
}

// A public article, the text is a preview when truncated
export interface PublicKnowledgeBaseArticle {
    id: number;
    subject: string;
    description: string | null;
    text: string | null;
    group: number;
    group_name: string;
    section: number | null;
    section_name: string | null;
    status: KnowledgeBaseArticleStatus;
    access: KnowledgeBaseArticleAccess;
    feature: string | null;
    feature_translation: string | null;
    packages: string[];
    is_truncated: boolean;
    author: KnowledgeBaseAuthor | null;
    modified: string;
}

// A section of a public group with its published articles
export interface PublicKnowledgeBaseSection {
    id: number | null;
    name: string | null;
    articles: KnowledgeBaseArticleListItem[];
}

// A public group with its sections
export interface PublicKnowledgeBaseGroup extends KnowledgeBaseGroup {
    sections: PublicKnowledgeBaseSection[];
}

// A selectable feature of an article
export interface KnowledgeBaseFeatureOption {
    id: string;
    feature: string;
    feature_type: 'main' | 'sub';
    feature_translation: string;
}

// Base path of the public knowledge base
export const KNOWLEDGE_BASE_PUBLIC_PATH = '/kennisbank';

// Base path of the knowledge base management pages
export const KNOWLEDGE_BASE_ADMIN_PATH = '/settings/admin/knowledge-base';

// Resolves a media url of the backend, local environments need the base url in front
export const resolveMediaUrl = (url: string | null | undefined): string | null => {
    if (!url) return null;
    if (url.startsWith('http') || process.env.NODE_ENV === 'production') return url;
    return `${baseUrl}${url}`;
};

// Describes how long ago an article was updated in rough steps, never more precise than a week
export const describeUpdatedAgo = (modified: string, t: (key: string, options?: Record<string, any>) => string): string => {
    const days = Math.floor((Date.now() - new Date(modified).getTime()) / (1000 * 60 * 60 * 24));
    if (days < 7) return t('knowledge_base.public.updated_this_week');
    if (days < 30) return t('knowledge_base.public.updated_weeks', { count: Math.floor(days / 7) });
    if (days < 365) return t('knowledge_base.public.updated_months', { count: Math.floor(days / 30) });
    return t('knowledge_base.public.updated_years', { count: Math.floor(days / 365) });
};

// Joins the names of authors into one written by sentence
export const describeAuthors = (authors: KnowledgeBaseAuthor[], t: (key: string, options?: Record<string, any>) => string): string => {
    const names = authors.map(author => author.name);
    if (names.length === 0) return '';
    const joined = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')}${t('knowledge_base.public.names_and')}${names[names.length - 1]}`;
    return t('knowledge_base.public.written_by', { names: joined });
};
